import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import {
  isBrowserOffline,
  isLocalhostHostname,
  shouldTriggerHealthRetry,
} from "./apiHealthGateUtils";
import { ApiHealthRecoveryContext } from "./ApiHealthRecoveryContext";

import { adminUiCopy } from "@/copy/adminUiCopy";
import { useApiHealthGate } from "@/hooks/health/useApiHealthGate";
import { API_HEALTH_EVENTS } from "@/services/health/ApiHealthEvents";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { VNextButton, VNextNotice } from "@/vnext/primitives";
import { RecoveryNoticesProvider } from "@/vnext/shared/RecoveryNoticesContext";
import "@/vnext/routes/dashboard/dashboardMake.css";

interface ApiHealthGateProps {
  children: ReactNode;
}

interface ApiHealthDownStateProps {
  content: (typeof adminUiCopy.apiHealthGate.down)[keyof typeof adminUiCopy.apiHealthGate.down];
  isRetrying: boolean;
  onRetry: () => void;
}

const logger = FrontendLoggerFactory.getLogger().withComponent("ApiHealthGate");

const ApiHealthShell = ({
  children,
  subtitle,
  title,
}: {
  children: ReactNode;
  subtitle: string;
  title: string;
}) => (
  <AdminVNextShell subtitle={subtitle} title={title}>
    {children}
  </AdminVNextShell>
);

const ApiHealthCheckingState = () => (
  <ApiHealthShell
    subtitle={adminUiCopy.apiHealthGate.checkingDescription}
    title={adminUiCopy.apiHealthGate.checkingTitle}
  >
    <section className="dashboard-make-card dashboard-make-alert">
      <div className="dashboard-make-alert__body">
        <div className="dashboard-make-alert__icon dashboard-make-alert__icon--neutral">
          <span className="dashboard-make-gate-spinner" />
        </div>
        <div className="dashboard-make-alert__copy">
          <span className="dashboard-make-alert__title">
            {adminUiCopy.apiHealthGate.checkingTitle}
          </span>
          <span className="dashboard-make-alert__pill dashboard-make-alert__pill--neutral">
            {adminUiCopy.apiHealthGate.checkingStatus}
          </span>
          <span className="dashboard-make-alert__description">
            {" "}
            {adminUiCopy.apiHealthGate.checkingDescription}
          </span>
        </div>
      </div>
    </section>

    <section className="dashboard-make-health">
      <div className="dashboard-make-summary-heading">
        <span>{adminUiCopy.apiHealthGate.systemHealth}</span>
      </div>
      <div className="dashboard-make-health-grid">
        <article className="dashboard-make-card dashboard-make-section">
          <div className="dashboard-make-section__header">
            <h2 className="dashboard-make-section__title">
              {adminUiCopy.apiHealthGate.routeShellTitle}
            </h2>
            <span className="dashboard-make-health__status dashboard-make-health__status--neutral">
              {adminUiCopy.apiHealthGate.routeShellStatus}
            </span>
          </div>
          <div className="dashboard-make-section__body">
            <p className="dashboard-make-section__description">
              {adminUiCopy.apiHealthGate.routeShellDescription}
            </p>
            <div
              aria-live="polite"
              className="dashboard-make-gate-status"
              role="status"
            >
              {adminUiCopy.apiHealthGate.routeShellLiveStatus}
            </div>
          </div>
        </article>
      </div>
    </section>
  </ApiHealthShell>
);

const ApiHealthDownState = ({
  content,
  isRetrying,
  onRetry,
}: ApiHealthDownStateProps) => (
  <ApiHealthShell subtitle={content.message} title={content.title}>
    <section className="dashboard-make-card dashboard-make-alert">
      <div className="dashboard-make-alert__body">
        <div className="dashboard-make-alert__icon">
          <span className="dashboard-make-gate-alert-mark">!</span>
        </div>
        <div className="dashboard-make-alert__copy">
          <span className="dashboard-make-alert__title">
            {content.connectionLabel}
          </span>
          <span className="dashboard-make-alert__pill">
            {content.connectionStatus}
          </span>
          <span className="dashboard-make-alert__description">
            {" "}
            {content.recovery}
          </span>
        </div>
        <button
          aria-busy={isRetrying}
          className="dashboard-make-gate-button"
          disabled={isRetrying}
          onClick={onRetry}
          type="button"
        >
          {isRetrying
            ? adminUiCopy.apiHealthGate.retryChecking
            : adminUiCopy.apiHealthGate.retryConnection}
        </button>
      </div>
    </section>
  </ApiHealthShell>
);

const ApiHealthDegradedState = () => (
  <div className="dashboard-make-banner">
    <div className="dashboard-make-banner__inner">
      {adminUiCopy.apiHealthGate.degradedBanner}
    </div>
  </div>
);

// CRITICAL PD-51: an open page keeps its work during a transient outage. The
// notice must describe that state; the cold-load gate uses different copy.
// See docs/guides/PLAIN_LANGUAGE.md.
const ApiHealthDownNotice = ({
  content,
  isRetrying,
  onRetry,
}: ApiHealthDownStateProps) => (
  <VNextNotice
    action={
      <VNextButton
        aria-busy={isRetrying}
        disabled={isRetrying}
        onClick={onRetry}
        tone="secondary"
      >
        {isRetrying
          ? adminUiCopy.apiHealthGate.retryChecking
          : adminUiCopy.apiHealthGate.retryConnection}
      </VNextButton>
    }
    role="status"
    tone="warning"
  >
    {content.openMessage} {content.recovery}
  </VNextNotice>
);

const ApiHealthGate = ({ children }: ApiHealthGateProps) => {
  const { isRetrying, status, retry } = useApiHealthGate();

  const lastNetworkFailureAtRef = useRef<number>(0);
  const [hasEverBeenOk, setHasEverBeenOk] = useState(
    () => status === "ok" || status === "degraded",
  );
  const [recoveryGeneration, setRecoveryGeneration] = useState(0);
  const hadOutage = useRef(status === "down");
  const gateLogger = useMemo(() => logger, []);

  useEffect(() => {
    if (status === "down") {
      hadOutage.current = true;
    } else if (
      (status === "ok" || status === "degraded") &&
      hadOutage.current
    ) {
      hadOutage.current = false;
      setRecoveryGeneration((generation) => generation + 1);
    }
  }, [status]);

  useEffect(() => {
    gateLogger.info("Admin API health gate status updated", {
      operation: "api_health_gate_status_changed",
      status,
    });
    if ((status === "ok" || status === "degraded") && !hasEverBeenOk) {
      let isActive = true;
      queueMicrotask(() => {
        if (!isActive) {
          return;
        }

        setHasEverBeenOk(true);
      });

      return () => {
        isActive = false;
      };
    }

    return undefined;
  }, [gateLogger, hasEverBeenOk, status]);

  useEffect(() => {
    const unsubscribe = API_HEALTH_EVENTS.onNetworkFailure((payload) => {
      const now = Date.now();
      if (!shouldTriggerHealthRetry(now, lastNetworkFailureAtRef.current)) {
        gateLogger.debug(
          "Admin API health retry suppressed by debounce window",
          {
            operation: "api_health_gate_retry_debounced",
            url: payload.url ?? null,
            method: payload.method ?? null,
            status: payload.status ?? null,
          },
        );
        return;
      }

      lastNetworkFailureAtRef.current = now;
      gateLogger.warn("Admin API health retry triggered by transport failure", {
        operation: "api_health_gate_network_failure_retry",
        url: payload.url ?? null,
        method: payload.method ?? null,
        status: payload.status ?? null,
        message: payload.message ?? null,
      });
      retry("automatic");
    });

    return () => {
      unsubscribe();
    };
  }, [gateLogger, retry]);

  const browserNavigator =
    typeof navigator === "undefined" ? undefined : navigator;
  const isOffline = isBrowserOffline(browserNavigator);
  const windowHostname =
    typeof window !== "undefined" && typeof window.location !== "undefined"
      ? window.location.hostname
      : undefined;
  const isLocalhost = isLocalhostHostname(windowHostname);
  const downContent = isOffline
    ? adminUiCopy.apiHealthGate.down.offline
    : isLocalhost
      ? adminUiCopy.apiHealthGate.down.localhost
      : adminUiCopy.apiHealthGate.down.service;

  useEffect(() => {
    gateLogger.debug("Admin API health gate render state updated", {
      operation: "api_health_gate_render_state",
      status,
      hasEverBeenOk,
      isOffline,
      isLocalhost,
      downTitle: downContent.title,
    });
  }, [
    downContent.title,
    gateLogger,
    hasEverBeenOk,
    isLocalhost,
    isOffline,
    status,
  ]);

  const handleManualRetry = () => {
    gateLogger.info("Admin API health retry requested manually", {
      operation: "api_health_gate_retry_manual",
    });
    retry();
  };

  const shouldShowInitialLoading = status === "checking" && !hasEverBeenOk;

  if (shouldShowInitialLoading) {
    return <ApiHealthCheckingState />;
  }

  if (status === "down" && !hasEverBeenOk) {
    return (
      <ApiHealthRecoveryContext.Provider
        value={{ isDown: true, recoveryGeneration }}
      >
        <ApiHealthDownState
          content={downContent}
          isRetrying={isRetrying}
          onRetry={handleManualRetry}
        />
      </ApiHealthRecoveryContext.Provider>
    );
  }

  // CRITICAL PD-51: a later health outage must not hide an open editor or its
  // actions. Cold loads still block, and the server authorizes every write.
  // https://react.dev/reference/react/Activity
  const downNotice =
    status === "down" ? (
      <ApiHealthDownNotice
        content={downContent}
        isRetrying={isRetrying}
        onRetry={handleManualRetry}
      />
    ) : null;
  return (
    <ApiHealthRecoveryContext.Provider
      value={{ isDown: status === "down", recoveryGeneration }}
    >
      <RecoveryNoticesProvider
        notice={downNotice ? { id: "health", content: downNotice } : null}
      >
        {downNotice}
        {status === "degraded" ? <ApiHealthDegradedState /> : null}
        {children}
      </RecoveryNoticesProvider>
    </ApiHealthRecoveryContext.Provider>
  );
};

export default ApiHealthGate;
